import React, { useMemo } from 'react';
import { usePredictContext } from '~/hooks/predict/usePredictContext';
import { useAuth } from '~/state/auth';
import { WorkflowStage } from '~/types/predict';
import PatientBoardList from '~/components/patient-board/PatientBoardList';
import { OwnershipFilter } from '~/components/patient-board/types';
import { mapPredictPatient, makePredictConfig } from './predictToVM';

interface PredictPatientListProps {
  ownershipFilter?: OwnershipFilter;
}

/**
 * Predict Patient List — shares the Coordinate (discharge) patient-board UI.
 * Differences: a Score column (top program + fit score), phases Predict/Qualify/
 * Transfer, and no Urgency column. Disqualified and discharged patients are
 * hidden.
 */
const PredictPatientList: React.FC<PredictPatientListProps> = ({ ownershipFilter = 'ALL' }) => {
  const { patients: allPatients, takeOwnership } = usePredictContext();
  const { userEmail } = useAuth();

  const config = useMemo(() => makePredictConfig(takeOwnership), [takeOwnership]);

  const patients = useMemo(() => {
    const visible = allPatients.filter(
      p => !p.discharged?.completed && p.currentStage !== WorkflowStage.DISQUALIFIED,
    );
    const scoped =
      ownershipFilter === 'MY' ? visible.filter(p => p.assignment?.userId === userEmail) :
      ownershipFilter === 'UNASSIGNED' ? visible.filter(p => !p.assignment?.userId) :
      visible;
    return scoped.map(mapPredictPatient);
  }, [allPatients, ownershipFilter, userEmail]);

  return <PatientBoardList patients={patients} config={config} ownershipFilter={ownershipFilter} />;
};

export default PredictPatientList;
